import NProgress from 'nprogress'
import { embedlyCardSelector, embedlyGlobalSelector, setupEmbedly } from './embedly'
import { extraScrollMarginTop } from '../../css/redesign/exports/misc.scss'

// the default value is 'body'
// if we replace the whole body on browser back/forward
// we will lose content injected into the body (outside of unpoly_wrapper) by external JS scripts like Gleap
up.history.config.restoreTargets = ['.unpoly_wrapper']

// adding unpoly_wrapper to default render targets. When no other render target is given, Unpoly will pick these
// there are also defaults behaviors attached to main targets (update history, scroll to the top...)
up.layer.config.any.mainTargets.unshift('.unpoly_wrapper')

// turning off the autofocus behavior for now. When submitting a form by pressing enter, the focus usually shifts
// on a main element and since focus-visible identifies this as a keyboard driven event, an outline appears,
// which is unexpected for users who don't use accessibility features
up.fragment.config.autoFocus = false

// we sometimes want to execute <script> tags in fragments (Zapier)
up.script.config.scriptElementPolicy = 'auto'

// (temporarily) generate errors on functions to migrate for better visibility
up.migrate.config.logLevel = 'error'

const preRedesignErrorSelector = 'form .error'
const inputErrorSelector = '.form_control:has(input[aria-invalid="true"])'
const customScrollSelector = '.js-scroll_to_first'
const errorSelector = `${preRedesignErrorSelector},${inputErrorSelector},${customScrollSelector}`
up.compiler(errorSelector, { batch: true }, function ([firstError, ..._rest]) {
  if (!document.body.classList.contains('test')) {
    setTimeout(() => firstError.scrollIntoView(), 1)
  }
})

// workaround for Unpoly not submitting submit button values
up.compiler('button[type=submit][name]', button => {
  const form = button.closest('form')
  const buttonAttrName = button.getAttribute('name')
  const buttonAttrValue = button.getAttribute('value')

  button.addEventListener('click', () => {
    if (form) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = buttonAttrName
      input.value = buttonAttrValue
      form.appendChild(input)
    }
  })
})

// no link should ever be be preloaded because doing the preload request
// saves flash messages and cookies in the session, which then
// get rendered on unrelated pages if the preloaded link wasn't immediately followed.
up.preloadEnabled = false

// An Unpoly request is a fetch and cannot follow a redirect chain that
// leaves our origin (e.g. the OAuth continue leg to a widget site).
// Endpoints that need to send the browser elsewhere respond with this
// header instead of a redirect; we abort the fragment update and
// navigate for real.
up.on('up:fragment:loaded', event => {
  const location = event.response.header('x-steady-full-page-redirect')

  if (location) {
    event.preventDefault()
    up.network.loadPage({ url: location })
  }
})

up.macro('[unpoly-link]', function (link) {
  const unpolyLinkValue = link.getAttribute('unpoly-link')

  if (!unpolyLinkValue || unpolyLinkValue === 'true') {
    link.setAttribute('up-target', '.unpoly_wrapper')
  } else {
    link.setAttribute('up-target', unpolyLinkValue)
  }

  link.setAttribute('up-instant', 'true')
})

up.macro('[unpoly-link-no-cache]', function (link) {
  const unpolyLinkValue = link.getAttribute('unpoly-link')

  if (!unpolyLinkValue || unpolyLinkValue === 'true') {
    link.setAttribute('up-target', '.unpoly_wrapper')
  } else {
    link.setAttribute('up-target', unpolyLinkValue)
  }

  link.setAttribute('up-instant', 'true')
  link.setAttribute('up-cache', 'false')
})

NProgress.configure({ showSpinner: false })

// Disable the default progress bar
up.network.config.progressBar = false
up.on('up:network:late', () => {
  NProgress.start()
})
up.on('up:network:recover', () => {
  NProgress.done()
})

// When a non-Unpoly link with a hash location is clicked:
// - When hash location changes, Unpoly hijacks the scroll
//   So we need to tell Unpoly about the right offsets.
// - When the hash location does not change, the browser handles the scroll
//   respecting the scroll-margin-top CSS.
up.viewport.config.fixedTopSelectors = ['.js-smooth_scroll_top_offset']
up.viewport.config.revealPadding = parseInt(extraScrollMarginTop)

up.compiler(`${embedlyCardSelector}, ${embedlyGlobalSelector}`, function (element) {
  if (!document.body.classList.contains('test')) {
    setupEmbedly(element)
  }
})

up.compiler('.js-change_cookies_settings', element => {
  element.addEventListener('click', () => {
    if (!document.body.querySelector('.is-visible-cookie-banner')) {
      window.Cookiebot?.show()
    }
  })
})

up.compiler('.js-credit_card_form', function (element) {
  import(/* webpackChunkName: "three_ds2_credit_card_form" */ './three_ds2_credit_card_form.js')
    .then(async ({ default: ThreeDS2CreditCardForm }) => {
      new ThreeDS2CreditCardForm('.js-credit_card_form').init()
    })
    // eslint-disable-next-line no-console
    .catch(error => console.error('An error occurred while loading ThreeDS2CreditCardForm', error))
})

up.compiler('.js-apple_pay', function (element) {
  import(/* webpackChunkName: "apple_pay" */ './apple_pay.js')
    .then(async ({ default: ApplePay }) => {
      new ApplePay(element).init()
    })
    // eslint-disable-next-line no-console
    .catch(error => console.error('An error occurred while loading ApplePay', error))
})

up.compiler('.js-rich_text_editor', function (element) {
  let cleanUp

  import(/* webpackChunkName: "rte" */ './rich_text_editor.jsx')
    .then(({ default: renderRichTextEditor }) => {
      cleanUp = renderRichTextEditor(element)
    })
    .catch(error => console.error('An error occurred while loading RichTextEditor', error)) // eslint-disable-line no-console

  return () => {
    if (cleanUp) {
      cleanUp(element)
    }
  }
})

up.compiler('.js-multimedia_text_editor', function (element) {
  let cleanUp

  import(/* webpackChunkName: "mte" */ './multimedia_text_editor')
    .then(({ mount: renderMultimediaTextEditor }) => {
      cleanUp = renderMultimediaTextEditor(element)
    })
    .catch(error => console.error('An error occurred while loading MultimediaTextEditor', error)) // eslint-disable-line no-console

  return () => {
    if (cleanUp) {
      cleanUp(element)
    }
  }
})

up.compiler('.js-date_picker', function (element) {
  let cleanUp

  import(/* webpackChunkName: "datepicker" */ './date_picker.jsx')
    .then(({ mount: renderDatePicker }) => {
      cleanUp = renderDatePicker(element)
    })
    .catch(error => console.error('An error occurred while loading DatePicker', error)) // eslint-disable-line no-console

  return () => {
    if (cleanUp) {
      cleanUp(element)
    }
  }
})

up.compiler('.js-forbid_newlines', el => {
  el.addEventListener(
    'input',
    event => {
      event.target.value = event.target.value.replace(/[\r\n\v]+/g, '')
    },
    true
  )
})

up.compiler('[data-update-from-input]', el => {
  const defaultValue = el.dataset.updateFromInputDefaultValue
  const updateValue = value => {
    const newValue = value === '' && defaultValue ? defaultValue : value
    el.innerText = newValue
  }

  const input = document.querySelector(el.dataset.updateFromInput)

  if (input) {
    updateValue(input.value)
    input.addEventListener('input', ev => updateValue(ev.target.value))
  }
})

// disables submitting when <Enter> is pressed in inputs that are children of
// the element with this class; ignores textareas
up.compiler('.js-no_submit_on_enter', el => {
  function doNotSubmitOnEnter(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  const formControlsExceptTextareas = Array.from(el.querySelectorAll('input, select, button'))

  formControlsExceptTextareas.forEach(el => {
    el.addEventListener('keydown', doNotSubmitOnEnter)
  })
})

// history updates meta tags
up.history.config.updateMetaTags = true

// we sometimes want to propagate other things in the head (custom head for publications)
// this code replaces everything in the <head>-tag in the dynamic tags section, which is enclosed by
// two custom script tags.
// the dynamic tags to insert are defined as a template element:
// <template id="dynamic_head_content"><%= render("_dynamic_head_content.html", assigns) %></template>
// in the ".unpoly_wrapper".
// main reason: crawlers using JS (and so unpoly) get the right head content.
up.compiler('.unpoly_wrapper', el => {
  const dynamicSectionStartSelector = 'script[type="steady/dynamic-head-content-start"]'
  const dynamicSectionEndSelector = 'script[type="steady/dynamic-head-content-end"]'

  const dynamicSectionStart = document.querySelector(dynamicSectionStartSelector)
  const dynamicSectionEnd = document.querySelector(dynamicSectionEndSelector)

  let nextElementSibling = dynamicSectionStart && dynamicSectionStart.nextElementSibling

  while (nextElementSibling && nextElementSibling !== dynamicSectionEnd) {
    nextElementSibling = nextElementSibling.nextElementSibling
    nextElementSibling.previousElementSibling.remove()
  }

  const newDynamicContent = document.querySelector('#dynamic_head_content')
  if (newDynamicContent) {
    document
      .querySelector('head')
      .insertBefore(newDynamicContent.content, document.querySelector(dynamicSectionEndSelector))
    // Evaluate all scripts inserted dynamically that have type "text/plain" and contain the attribute "data-cookieconsent".
    window.Cookiebot && typeof window.Cookiebot.runScripts === 'function' && window.Cookiebot.runScripts()
  }
})

// a workaround for fetching images with a srcset in Safari
// see https://github.com/unpoly/unpoly/issues/175
up.compiler('img[srcset]', function (el) {
  const srcset = el.getAttribute('srcset')
  el.setAttribute('srcset', srcset)
})

up.compiler('video', async function (video) {
  // See: https://github.com/unpoly/unpoly/issues/432
  // by trial an error, I noticed that whenever the video didn't load due to the bug/
  // video.networkState was equal to zero, but when the bug doesn't appear, it's greater than zero.
  // I suspect this will lead to false positives because
  // most likely it is also equal to zero right before the video loads correctly.
  // But this is the best I could find.
  if (video.networkState === 0) {
    // If video didn't load in Safari and it's autoplaying, just play it.
    // If it's not autoplaying, replace the video with an image (its poster) and a link to the video file.
    if (video.autoplay) {
      try {
        await video.play()
      } catch (err) {
        // errors do not matter
      }
    } else {
      const img = document.createElement('img')
      img.setAttribute('width', video.getAttribute('width'))
      img.setAttribute('height', video.getAttribute('height'))
      img.setAttribute('src', video.getAttribute('poster'))
      img.setAttribute('aria-hidden', 'true')
      img.setAttribute('style', 'display:block;border-radius:inherit;')
      const link = document.createElement('a')
      const linkTitle = `${video.getAttribute('title')} (${window.steadyI18n.externalLinkWarning})`
      link.setAttribute('title', linkTitle)
      link.setAttribute('aria-label', linkTitle)
      link.setAttribute('href', video.querySelector('source').src)
      link.setAttribute('target', '_blank')
      video.classList.forEach(className => link.classList.add(className))

      link.appendChild(img)
      video.parentNode.replaceChild(link, video)
    }
  }
})
